import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, Key, Shield, Mail, Phone, MapPin, User, Bell, Zap, Award } from 'lucide-react';
import { motion } from 'framer-motion';

export function ProfilePage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Here you would make an API call to update the profile
      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update profile',
      });
    }
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <h1 className="text-3xl font-bold tracking-tight text-primary-700 dark:text-primary-300">
          My Profile
        </h1>
        <div className="text-sm px-4 py-2 rounded-full glass-effect text-muted-foreground">
          Member since {new Date().getFullYear() - 1}
        </div>
      </motion.div>

      <Tabs defaultValue="general" className="space-y-8">
        <TabsList className="bg-background/50 backdrop-blur-sm">
          <TabsTrigger value="general" className="data-[state=active]:bg-primary/10">
            <User className="h-4 w-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-primary/10">
            <Shield className="h-4 w-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="preferences" className="data-[state=active]:bg-primary/10">
            <Bell className="h-4 w-4 mr-2" />
            Preferences
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          {/* Profile Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Manage your personal information</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="relative mx-auto md:mx-0">
                    <Avatar variant="glowing" className="h-32 w-32">
                      <AvatarImage src={user?.avatar} />
                      <AvatarFallback className="text-3xl bg-primary/10 text-primary">
                        {user?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <Button
                      size="icon"
                      variant="secondary"
                      className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full shadow-lg"
                    >
                      <Camera className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="text-center md:text-left">
                    <h2 className="text-2xl font-bold text-primary">{user?.name}</h2>
                    <div className="flex items-center justify-center md:justify-start mt-1">
                      <div className="px-3 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary flex items-center">
                        <Award className="h-3 w-3 mr-1" />
                        <span className="capitalize">{user?.role.replace('_', ' ')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleUpdateProfile} className="mt-8 space-y-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-foreground/80">Full Name</Label>
                      <Input
                        id="name"
                        defaultValue={user?.name}
                        disabled={!isEditing}
                        className={isEditing ? "bg-background/50 backdrop-blur-sm border-primary/20" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-foreground/80">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        defaultValue={user?.email}
                        disabled={!isEditing}
                        className={isEditing ? "bg-background/50 backdrop-blur-sm border-primary/20" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-foreground/80">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        defaultValue="(555) 123-4567"
                        disabled={!isEditing}
                        className={isEditing ? "bg-background/50 backdrop-blur-sm border-primary/20" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location" className="text-foreground/80">Location</Label>
                      <Input
                        id="location"
                        defaultValue="New York, NY"
                        disabled={!isEditing}
                        className={isEditing ? "bg-background/50 backdrop-blur-sm border-primary/20" : ""}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-4">
                    {isEditing ? (
                      <>
                        <Button
                          type="button"
                          variant="glass"
                          onClick={() => setIsEditing(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">Save Changes</Button>
                      </>
                    ) : (
                      <Button type="button" onClick={() => setIsEditing(true)}>
                        Edit Profile
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>Your primary contact details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-3">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background/30 backdrop-blur-sm transition-all hover:bg-background/50">
                    <div className="rounded-full bg-primary/10 p-3">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-sm text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background/30 backdrop-blur-sm transition-all hover:bg-background/50">
                    <div className="rounded-full bg-primary/10 p-3">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Phone</p>
                      <p className="text-sm text-muted-foreground">(555) 123-4567</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background/30 backdrop-blur-sm transition-all hover:bg-background/50">
                    <div className="rounded-full bg-primary/10 p-3">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Location</p>
                      <p className="text-sm text-muted-foreground">New York, NY</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          {/* Security Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>Manage your account security</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-xl bg-background/30 backdrop-blur-sm">
                    <div className="rounded-full bg-primary/10 p-3 md:mx-0 mx-auto">
                      <Key className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <p className="font-medium">Password</p>
                      <p className="text-sm text-muted-foreground">
                        Last changed 3 months ago
                      </p>
                    </div>
                    <Button variant="glass">Change Password</Button>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-xl bg-background/30 backdrop-blur-sm">
                    <div className="rounded-full bg-primary/10 p-3 md:mx-0 mx-auto">
                      <Shield className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <p className="font-medium">Two-Factor Authentication</p>
                      <p className="text-sm text-muted-foreground">
                        Add an extra layer of security
                      </p>
                    </div>
                    <Button variant="glass">Enable 2FA</Button>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-xl bg-background/30 backdrop-blur-sm">
                    <div className="rounded-full bg-primary/10 p-3 md:mx-0 mx-auto">
                      <Zap className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <p className="font-medium">Active Sessions</p>
                      <p className="text-sm text-muted-foreground">
                        Manage your active login sessions
                      </p>
                    </div>
                    <Button variant="glass">View Sessions</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="preferences" className="space-y-6">
          {/* Notification Preferences */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>Manage how you receive notifications</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-center text-muted-foreground py-8">
                  Notification preferences settings coming soon...
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}